import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AdminDataManagementService} from '@6grain/masofa-monolith-api-client/common/api/adminDataManagement.service';
import {DbContextInfo} from '@6grain/masofa-monolith-api-client/common/model/dbContextInfo';
import {TableInfo} from '@6grain/masofa-monolith-api-client/common/model/tableInfo';
import {ColumnInfo} from '@6grain/masofa-monolith-api-client/common/model/columnInfo';
import {GetTableDataRequest} from '@6grain/masofa-monolith-api-client/common/model/getTableDataRequest';
import {TableDataResult} from '@6grain/masofa-monolith-api-client/common/model/tableDataResult';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {CardModule} from 'primeng/card';
import {TableModule} from 'primeng/table';
import {PaginatorModule} from 'primeng/paginator';
import {TooltipModule} from 'primeng/tooltip';
import {InputTextModule} from 'primeng/inputtext';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {ListPageWrapper} from '@app/wrappers/pages/list-page-wrapper/list-page-wrapper';
import {ToastModule} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {Subject, takeUntil} from 'rxjs';
import {Loading} from '@components/ui/loading/loading';

@Component({
  selector: 'app-data-management-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonModule, DialogModule, CardModule, TableModule, PaginatorModule, TooltipModule, ToastModule, ListPageWrapper, InputTextModule, ProgressSpinnerModule, Loading],
  templateUrl: './data-management-page.html',
  styleUrls: ['./data-management-page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService]
})
export class DataManagementPageComponent implements OnInit, OnDestroy {
  dbContexts: DbContextInfo[] = [];
  selectedContext: DbContextInfo | null = null;
  tables: TableInfo[] = [];
  loading = false;

  // Structure modal
  showStructureModal = false;
  structureColumns: ColumnInfo[] = [];
  structureTableName = '';
  loadingStructure = false;

  // Data modal
  showDataModal = false;
  dataResult: TableDataResult | null = null;
  dataPage = 1;
  dataPageSize = 20;
  dataSortBy: string | undefined = undefined;
  dataSortDesc = false;
  dataEntityTypeName = '';
  dataTableName = '';
  dataFilters: { [key: string]: any } = {};
  filterSearchText = '';
  loadingData = false;
  loadingDataModal = false;

  // Export modal
  showExportModal = false;
  exportLoading = false;

  private destroy$ = new Subject<void>();

  constructor(
    private adm: AdminDataManagementService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadContexts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadContexts(): void {
    this.loading = true;
    this.adm.commonAdminDataManagementGetDbContextsGet()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ctx => {
          this.dbContexts = ctx || [];
          if (!this.selectedContext && this.dbContexts.length) {
            this.selectedContext = this.dbContexts[0];
            this.onContextChange();
          }
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.dbContexts = [];
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  onContextChange(): void {
    if (!this.selectedContext || !this.selectedContext.fullName) return;
    this.loading = true;
    this.adm.commonAdminDataManagementGetTablesGet(this.selectedContext.fullName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: t => {
          this.tables = (t || []).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.tables = [];
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  openStructure(table: TableInfo): void {
    if (!this.selectedContext || !this.selectedContext.fullName) return;
    this.structureTableName = table.name ?? '';
    if (!table.entityTypeName) return;
    this.loadingStructure = true;
    this.cdr.detectChanges();
    this.adm.commonAdminDataManagementGetTableStructureGet(this.selectedContext.fullName, table.entityTypeName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: cols => {
          this.structureColumns = cols || [];
          this.showStructureModal = true;
          this.loadingStructure = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.structureColumns = [];
          this.loadingStructure = false;
          this.cdr.detectChanges();
        }
      });
  }

  openData(table: TableInfo): void {
    if (!this.selectedContext || !this.selectedContext.fullName) return;
    if (!table.entityTypeName) return;
    this.loadingDataModal = true;
    this.dataEntityTypeName = table.entityTypeName;
    this.dataTableName = table.name ?? table.entityTypeName;
    this.filterSearchText = '';
    this.dataFilters = {};
    this.fetchDataPage(1);
  }

  fetchDataPage(page: number): void {
    if (!this.selectedContext || !this.selectedContext.fullName || !this.dataEntityTypeName) return;
    this.dataPage = page;
    this.loadingData = true;
    this.cdr.detectChanges();
    
    const req: GetTableDataRequest = {
      dbContextFullName: this.selectedContext.fullName,
      entityTypeName: this.dataEntityTypeName,
      take: this.dataPageSize,
      offset: (this.dataPage - 1) * this.dataPageSize,
      sortBy: this.dataSortBy,
      sortDescending: this.dataSortDesc,
      filters: this.dataFilters
    } as any;
    
    this.adm.commonAdminDataManagementGetTableDataPost(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.dataResult = res || { data: [], totalCount: 0 } as TableDataResult;
          this.showDataModal = true;
          this.loadingData = false;
          this.loadingDataModal = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.dataResult = { data: [], totalCount: 0 } as TableDataResult;
          this.loadingData = false;
          this.loadingDataModal = false;
          this.cdr.detectChanges();
        }
      });
  }

  applyFilter(): void {
    // Build filters from search text
    this.dataFilters = {};
    if (this.filterSearchText && this.filterSearchText.trim()) {
      // Simple text search - will search in all string fields
      // For more complex filtering, you can extend this
      this.dataFilters['search'] = this.filterSearchText.trim();
    }
    this.fetchDataPage(1);
  }

  clearFilter(): void {
    this.filterSearchText = '';
    this.dataFilters = {};
    this.fetchDataPage(1);
  }

  openExportModal(): void {
    this.showExportModal = true;
  }

  closeExportModal(): void {
    this.showExportModal = false;
  }

  exportToCSV(): void {
    if (!this.selectedContext || !this.selectedContext.fullName || !this.dataEntityTypeName) return;
    
    this.exportLoading = true;
    this.closeExportModal();
    this.cdr.detectChanges();

    const maxRecords = 1000;
    const req: GetTableDataRequest = {
      dbContextFullName: this.selectedContext.fullName,
      entityTypeName: this.dataEntityTypeName,
      take: maxRecords,
      offset: 0,
      sortBy: this.dataSortBy,
      sortDescending: this.dataSortDesc,
      filters: this.dataFilters
    } as any;

    this.adm.commonAdminDataManagementGetTableDataPost(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const data = res?.data || [];
          const totalCount = res?.totalCount || 0;
          const exportCount = Math.min(data.length, maxRecords);
          
          if (exportCount === 0) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('Warning'),
              detail: this.translate.instant('No data to export')
            });
            this.exportLoading = false;
            this.cdr.detectChanges();
            return;
          }

          // Convert to CSV
          const csvContent = this.convertToCSV(data);
          this.downloadCSV(csvContent, `${this.dataTableName}_export.csv`);
          
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('Success'),
            detail: this.translate.instant('Data exported successfully')
          });
          
          this.exportLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('Error'),
            detail: this.translate.instant('Failed to export data')
          });
          this.exportLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private convertToCSV(data: any[]): string {
    if (!data || data.length === 0) return '';

    // Get headers from first row
    const headers = Object.keys(data[0]);
    
    // Create CSV rows
    const rows = [headers.join(',')];
    
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        // Escape commas and quotes in values
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      });
      rows.push(values.join(','));
    });

    return rows.join('\n');
  }

  private downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Helpers for template to avoid Object usage and null errors
  get headerColumns(): string[] {
    const first = this.dataResult?.data && this.dataResult.data.length > 0 ? this.dataResult.data[0] : undefined;
    return first ? Object.keys(first) : [];
  }

  rowKeys(row: any): string[] {
    return row ? Object.keys(row) : [];
  }

  formatCellValue(value: any): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'boolean') return value ? '✓' : '✗';
    return String(value);
  }

  getTotalPages(totalCount: number): number {
    if (!totalCount || totalCount === 0) return 1;
    return Math.ceil(totalCount / this.dataPageSize);
  }

  isCopyableColumn(column: string): boolean {
    const normalized = column?.toLowerCase() ?? '';
    return normalized === 'id' || normalized.endsWith('id');
  }

  async copyCellValue(value: unknown): Promise<void> {
    if (value === null || value === undefined) {
      return;
    }
    try {
      await navigator.clipboard.writeText(String(value));
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('Copied'),
        detail: this.translate.instant('Value copied to clipboard')
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Error'),
        detail: this.translate.instant('Copy failed')
      });
    }
  }
}


